/**
 * InspectionDetails Component (GraphQL version)
 * Displays inspection details using GraphQL API via services-px
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Paper, Box, Typography, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import {
	getInspectionDetails,
	clearInspectionDetails,
} from './redux/slice';
import StaticInfoSection from './components/staticInfoSection';
import AssetDetailsSection from './components/assetDetailsSection';
import InspectionZones from './components/inspectionZones';
import './inspectionDetails.scss';

const InspectionDetails = () => {
	const { inspectionId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { messages } = useIntl();
	const dispatch = useDispatch();

	// Access state directly in the UI
	const inspection = useSelector((state) => state.inspectionDetailsRedux?.inspection);
	const staticInfo = useSelector((state) => state.inspectionDetailsRedux?.staticInfo);
	const assetData = useSelector((state) => state.inspectionDetailsRedux?.assetData || []);
	const inspectionPhotos = useSelector((state) => state.inspectionDetailsRedux?.inspectionPhotos || []);
	const loading = useSelector((state) => state.inspectionDetailsRedux?.loading);
	const error = useSelector((state) => state.inspectionDetailsRedux?.error);
	const companyId = useSelector((state) => state.securityRedux?.user?.companyId);

	const [defectsOnly, setDefectsOnly] = useState(true);

	// Fetch inspection details when we have both IDs
	useEffect(() => {
		if (inspectionId && companyId) {
			dispatch(getInspectionDetails({ inspectionId, companyId }));
		}
	}, [inspectionId, companyId, dispatch]);

	// Separate cleanup effect that only runs on unmount
	useEffect(() => {
		return () => {
			dispatch(clearInspectionDetails());
		};
	}, []);

	const returnPath = location.state?.returnPath ||
		sessionStorage.getItem('inspectionDetails_returnPath') ||
		'/app/evir-px';
	const returnTitle = location.state?.returnTitle ||
		sessionStorage.getItem('inspectionDetails_returnTitle') ||
		messages['evirPx_inspection_history'];

	const handleBackNavigation = () => {
		sessionStorage.removeItem('inspectionDetails_returnPath');
		sessionStorage.removeItem('inspectionDetails_returnTitle');
		// Use browser history when navigated here normally (preserves history stack)
		// Fall back to navigate() for deep-links/bookmarks
		if (location.state?.returnPath) {
			window.history.back();
		} else {
			navigate(returnPath);
		}
	};

	const getInspectionType = () => {
		if (location.state?.inspectionType) return location.state.inspectionType;
		if (staticInfo?.['inspection type']) return staticInfo['inspection type'];
		// GraphQL returns name directly on the inspection
		if (inspection?.name) return inspection.name;
		return messages.inspection || 'Inspection';
	};

	const handleDefectsOnlyToggle = () => {
		setDefectsOnly(!defectsOnly);
	};

	if (loading) {
		return (
			<Container maxWidth={false} className="inspection-details-page">
				<Paper className="inspection-details-container">
					<Box className="loading-container">
						<CircularProgress />
					</Box>
				</Paper>
			</Container>
		);
	}

	if (error) {
		return (
			<Container maxWidth={false} className="inspection-details-page">
				<Paper className="inspection-details-container">
					<Box className="error-container" style={{ padding: '20px', textAlign: 'center' }}>
						<Typography color="error">
							{messages.errorLoadingInspection || 'Error loading inspection details'}: {error}
						</Typography>
						<button onClick={handleBackNavigation} style={{ marginTop: '20px' }}>
							{messages.goBack || 'Go Back'}
						</button>
					</Box>
				</Paper>
			</Container>
		);
	}

	return (
		<Container maxWidth={false} className="inspection-details-page">
			<Paper className="inspection-details-container">
				<Box className="inspection-header">
					<Box className="header-left">
						<a
							className="back-link"
							onClick={handleBackNavigation}
							style={{ cursor: 'pointer' }}
						>
							&lt; {messages.backTo || 'Back to'} {returnTitle}
						</a>
						<Typography variant="h1" className="page-title">
							{getInspectionType()}
						</Typography>
					</Box>
					<Box className="header-right">
						<FormControlLabel
							control={
								<Switch
									checked={defectsOnly}
									onChange={handleDefectsOnlyToggle}
									color="primary"
								/>
							}
							label={messages.defectsOnly || 'Defects Only'}
							className="defects-toggle"
						/>
					</Box>
				</Box>

				<Box className="inspection-content">
					{inspection && staticInfo ? (
						<>
							<Box className="details-columns">
								<Box className="details-column left">
									<AssetDetailsSection assetData={assetData} />
								</Box>
								<Box className="details-column right">
								<StaticInfoSection staticInfo={staticInfo} inspectionPhotos={inspectionPhotos} />
							</Box>
						</Box>
							<InspectionZones assetData={assetData} defectsOnly={defectsOnly} />
						</>
					) : !loading && !error ? (
						<Box className="empty-state">
							<Typography variant="body1" color="textSecondary">
								{messages.noInspectionData || 'No inspection data available'}
							</Typography>
						</Box>
					) : null}
				</Box>
			</Paper>
		</Container>
	);
};

export default InspectionDetails;
